import { useEffect, useRef, useState } from 'react'
import { useVerifyEmailFlow } from '../hooks/useVerifyEmailFlow'
import { useAuth } from '../context/AuthContext'
import { RithmoWordmark } from './shell/OrgMark'
import { productBrand as brand } from './shell/brand'

/**
 * The customer build's email verification page, in the Control Board's sign-in layout, on Rithmo's own verification
 * flow (useVerifyEmailFlow: the same calls, resend wait and next stop as pages/VerifyEmailPage.jsx).
 */

export default function CustomerVerifyEmailPage() {
  const { email, verify, resend, loading, error, countdown, resending } = useVerifyEmailFlow()
  const { logout } = useAuth()
  const [digits, setDigits] = useState(['', '', '', '', '', ''])
  const inputs = useRef([])

  useEffect(() => { inputs.current[0]?.focus() }, [])

  const tryCode = async (next) => {
    if (next.some((d) => d === '')) return
    const ok = await verify(next.join(''))
    if (!ok) {
      setDigits(['', '', '', '', '', ''])
      setTimeout(() => inputs.current[0]?.focus(), 50)
    }
  }
  const change = (i, value) => {
    if (!/^\d?$/.test(value)) return
    const next = [...digits]
    next[i] = value
    setDigits(next)
    if (value && i < 5) inputs.current[i + 1]?.focus()
    if (value) void tryCode(next)
  }
  const paste = (e) => {
    e.preventDefault()
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (!text) return
    const next = Array.from({ length: 6 }, (_, i) => text[i] || '')
    setDigits(next)
    const empty = next.findIndex((d) => !d)
    inputs.current[empty >= 0 ? empty : 5]?.focus()
    void tryCode(next)
  }

  return (
    <div className="grid min-h-screen bg-gray-50 lg:grid-cols-2">
      <div className="hidden flex-col justify-between bg-brand-gradient p-12 text-white lg:flex">
        <div className="flex items-center gap-3">
          <RithmoWordmark size={36} onDark />
        </div>
        <div className="max-w-md">
          <h2 className="text-3xl font-bold leading-tight">One more step.</h2>
          <p className="mt-4 text-base leading-relaxed text-white/75">Confirm your email, then set up your organization’s Control Board.</p>
        </div>
        <p className="text-sm text-white/60">{brand.governedBy}</p>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <RithmoWordmark size={36} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Verify your email</h1>
          <p className="mt-2 text-sm text-gray-500">
            We sent a 6-digit code to <span className="font-semibold text-gray-700">{email || 'your email'}</span>. Enter it below.
          </p>
          {error && <div role="alert" className="mt-6 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

          <div className="mt-6 flex justify-between gap-2" aria-label="Verification code">
            {digits.map((d, i) => (
              <input key={i} ref={(el) => { inputs.current[i] = el }} type="text" inputMode="numeric" maxLength={1} pattern="[0-9]" value={d}
                aria-label={`Digit ${i + 1}`} disabled={loading} onChange={(e) => change(i, e.target.value)} onPaste={i === 0 ? paste : undefined}
                onKeyDown={(e) => { if (e.key === 'Backspace' && !digits[i] && i > 0) inputs.current[i - 1]?.focus() }}
                className="h-14 w-12 rounded-xl border border-gray-300 bg-white text-center text-2xl font-bold text-gray-900 outline-none focus:border-primary-400 disabled:opacity-50" />
            ))}
          </div>
          {loading && <p className="mt-4 text-sm text-gray-600">Checking…</p>}

          <p className="mt-6 text-sm text-gray-500">
            Didn’t get it?{' '}
            {countdown > 0 ? <span className="text-gray-600">Send again in {countdown}s</span> : (
              <button type="button" onClick={() => void resend()} disabled={resending} className="font-semibold text-primary-700 hover:text-primary-800 disabled:opacity-50">
                {resending ? 'Sending…' : 'Send a new code'}
              </button>
            )}
          </p>
          <button type="button" onClick={logout} className="mt-6 text-sm font-semibold text-gray-600 hover:text-gray-900">Use a different account</button>
        </div>
      </div>
    </div>
  )
}
